/**
 * PATH: packages/email-hub/src/feature/components/rich-text/emailHtml.ts
 * FILE_ID: EMAILHUB-RICHTEXT-EMAILHTML-001
 * PURPOSE: Pure-ish I→O transforms between the TipTap editor's native HTML
 *          (classes + data-attrs, round-trips into the editor for drafts)
 *          and EMAIL-SAFE HTML (inline styles only, sanitised) used at send.
 * WHY:     Email clients strip <style> blocks and unknown classes, so the
 *          editor's table borders, list markers, headings, colours and the
 *          embedded Kanban-card chip must be re-expressed as inline styles
 *          before sending. Formula cells bake to their computed value.
 * FLOW:
 *   editor.getHTML()  ──toEmailHtml──▶  inline-styled + sanitised HTML  ──▶ send
 *                      ──extractLinkedCardIds──▶ [cardId, …]  ──▶ allocations
 * DEPENDENCIES:
 *   - dompurify: final allowlist sanitise (defence-in-depth on send).
 *   - DOMParser (browser + jsdom). Guards for non-DOM environments.
 */

import DOMPurify from 'dompurify'
import { expandSignatureBlocks } from './signatureBlock'
import { expandQuoteBlocks } from './quoteBlock'

/** Does the operator's TYPING AREA (everything above the quoted /
 *  forwarded block) contain no real text yet? Used to keep the
 *  placeholder visible and to gate the reply-prefill rebuild.
 *  Pure: html -> boolean. (Lifted verbatim from the legacy editor so the
 *  useComposePage guards + placeholder tests are unchanged.) */
export function typingAreaIsBlank(html: string): boolean {
  if (!html) return true
  // Quote boundary markers. The legacy inline-style + "Forwarded message"
  // markers cover stale drafts; `<hr` and `data-quote` are the signals that
  // SURVIVE the TipTap round-trip (the editor flattens the old border-left
  // div but keeps the divider + any data-quote attribute), so detection
  // stays correct after the contentEditable→ProseMirror swap.
  const markerIdx = html.search(/border-left:3px solid #cbd5e1|-+\s*Forwarded message|data-quote=|<hr\b/i)
  let cut = html.length
  if (markerIdx >= 0) {
    const lt = html.lastIndexOf('<', markerIdx)
    cut = lt >= 0 ? lt : markerIdx
  }
  const slice = html.slice(0, cut)
  // Embedded media / cards are real content even though they strip to "".
  if (/<(img|table|hr|video|iframe)\b/i.test(slice)) return false
  if (/data-kanban-card/i.test(slice)) return false
  return slice
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .replace(/\s+/g, '')
    .length === 0
}

function parseDoc(html: string): Document | null {
  if (typeof DOMParser === 'undefined') return null
  try {
    return new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html')
  } catch {
    return null
  }
}

/** Collect the card ids embedded in the body so the send path can create
 *  the email<->card links. Pure: html -> string[] (deduped, order-stable). */
export function extractLinkedCardIds(html: string): string[] {
  const doc = parseDoc(html)
  if (!doc) return []
  const seen = new Set<string>()
  doc.querySelectorAll('[data-kanban-card]').forEach((el) => {
    const id = el.getAttribute('data-card-id')?.trim()
    if (id) seen.add(id)
  })
  return [...seen]
}

const STYLE = {
  table: 'border-collapse:collapse;width:100%;margin:8px 0;font-size:14px;',
  cell: 'border:1px solid #d1d5db;padding:6px 8px;vertical-align:top;',
  header: 'border:1px solid #d1d5db;padding:6px 8px;background:#f3f4f6;font-weight:600;text-align:left;',
  h1: 'font-size:1.6em;font-weight:700;line-height:1.25;margin:0.5em 0 0.35em;',
  h2: 'font-size:1.3em;font-weight:700;line-height:1.3;margin:0.5em 0 0.3em;',
  h3: 'font-size:1.1em;font-weight:600;line-height:1.35;margin:0.45em 0 0.25em;',
  li: 'margin:0.15em 0;',
  blockquote: 'border-left:3px solid #cbd5e1;margin:0.5em 0;padding-left:0.9em;color:#475569;font-style:italic;',
  a: 'color:#2563eb;text-decoration:underline;',
  img: 'max-width:100%;height:auto;',
} as const

/** Prepend default inline styling, preserving any author-set inline style
 *  (e.g. a per-cell background colour set in the editor). */
function prependStyle(el: Element, base: string): void {
  const existing = el.getAttribute('style') || ''
  el.setAttribute('style', existing ? `${base}${existing}` : base)
}

/** Depth-aware list marker so nested "sub-points" stay visually distinct in
 *  the SENT email, mirroring the editor (email clients strip the <style> the
 *  editor uses for `ul ul`). Level 0 = disc / decimal, then circle / square
 *  and lower-alpha / lower-roman as you nest deeper. Pure: (el) -> css. */
const UL_MARKERS = ['disc', 'circle', 'square'] as const
const OL_MARKERS = ['decimal', 'lower-alpha', 'lower-roman', 'upper-alpha', 'upper-roman'] as const
function listStyleFor(el: Element, ordered: boolean): string {
  let depth = 0
  for (let p = el.parentElement; p; p = p.parentElement) {
    if (p.tagName === 'UL' || p.tagName === 'OL') depth += 1
  }
  const markers = ordered ? OL_MARKERS : UL_MARKERS
  // Cycle (not clamp) so deeply nested lists keep alternating markers instead
  // of collapsing every level past the cap onto the same style.
  const marker = markers[depth % markers.length]
  const margin = depth === 0 ? 'margin:0.5em 0;' : 'margin:0.2em 0;'
  return `list-style:${marker} outside;${margin}padding-left:1.6em;`
}

/** Replace the editor's Kanban-card NodeView markup with a static,
 *  inline-styled anchor chip that renders in any email client. */
function bakeCardChips(doc: Document): void {
  doc.querySelectorAll('[data-kanban-card]').forEach((el) => {
    const title = el.getAttribute('data-card-title') || 'Kanban card'
    const subtitle = el.getAttribute('data-card-subtitle') || ''
    const status = el.getAttribute('data-card-status') || ''
    const url = el.getAttribute('data-card-url') || ''
    const a = doc.createElement('a')
    if (url) {
      a.setAttribute('href', url)
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener noreferrer')
    }
    a.setAttribute(
      'style',
      'display:inline-block;max-width:100%;border:1px solid #c7d2fe;background:#eef2ff;'
      + 'border-radius:8px;padding:6px 10px;margin:2px 0;color:#3730a3;text-decoration:none;'
      + 'font-size:13px;line-height:1.3;',
    )
    const safeTitle = title.replace(/</g, '&lt;')
    const metaParts = [status, subtitle].filter(Boolean).join(' &middot; ')
    a.innerHTML =
      `<strong>\u{1F4CB} ${safeTitle}</strong>`
      + (metaParts ? `<br><span style="color:#6366f1;font-size:11px;">${metaParts.replace(/</g, '&lt;')}</span>` : '')
    el.replaceWith(a)
  })
}

/** Strip the live-formula data so the sent cell carries only its computed
 *  text value (already rendered into the cell while composing). */
function bakeFormulas(doc: Document): void {
  doc.querySelectorAll('[data-formula]').forEach((el) => el.removeAttribute('data-formula'))
}

/** Bake the editor's image alignment (`data-align`) into an email-safe
 *  wrapper. The node stores alignment only as a `data-align` attribute, which
 *  every mail client ignores, so an aligned image arrived left-stuck
 *  (operator 2026-06-29). We wrap the image in a `<div style="text-align:…">`
 *  and make the image `inline-block` — `text-align` on a block parent aligns
 *  an inline-level child in EVERY client (incl. Outlook desktop), unlike
 *  `margin:auto`. Pure: mutates the doc in place. */
function bakeImageAlign(doc: Document): void {
  doc.querySelectorAll('img[data-align]').forEach((img) => {
    const align = (img.getAttribute('data-align') || '').trim()
    img.removeAttribute('data-align')
    if (align !== 'left' && align !== 'center' && align !== 'right') return
    const prevStyle = img.getAttribute('style') || ''
    img.setAttribute('style', `${prevStyle}display:inline-block;`)
    const wrap = doc.createElement('div')
    wrap.setAttribute('style', `text-align:${align};`)
    img.replaceWith(wrap)
    wrap.appendChild(img)
  })
}

/** Drop EMPTY list items (and lists that become empty) before send.
 *  WHY: indenting with Tab seeds blank sub-bullets; if the operator never
 *  types in them they ship as phantom markers. Outlook desktop stacks them
 *  on their own indented lines, but the Quotech reader (Tailwind `prose`)
 *  collapses zero-height empties so their markers pile onto ONE line
 *  ("◦ ■ •"), reading like the indentation broke (operator 2026-06-29).
 *  Removing them makes every client agree: clean output, no slop. An `<li>`
 *  is "empty" only when it has NO text AND no real media/card AND no nested
 *  list that still carries content (recursive `textContent` covers the
 *  nested case). Iterates so a chain of nested empties collapses bottom-up. */
function pruneEmptyListItems(doc: Document): void {
  const MEDIA = 'img,table,hr,video,iframe,[data-kanban-card]'
  const isBlank = (el: Element): boolean =>
    (el.textContent || '').replace(/\u00a0/g, '').replace(/\s+/g, '').length === 0
    && !el.querySelector(MEDIA)
  // Bounded loop: each pass removes a layer of leaf empties, then any list
  // left with no items. Guard against pathological input.
  for (let pass = 0; pass < 20; pass += 1) {
    let changed = false
    doc.querySelectorAll('li').forEach((li) => {
      if (isBlank(li)) { li.remove(); changed = true }
    })
    doc.querySelectorAll('ul,ol').forEach((list) => {
      if (!list.querySelector(':scope > li')) { list.remove(); changed = true }
    })
    if (!changed) break
  }
}

/**
 * Convert the editor's native HTML into email-safe HTML:
 *  inline styles for tables/lists/headings/quotes/links/images, baked card
 *  chips, baked formula values, then a final DOMPurify allowlist sanitise.
 *  Pure-ish: (editorHtml) -> emailHtml. Falls back to a sanitise-only pass
 *  when DOMParser is unavailable.
 */
export function toEmailHtml(editorHtml: string): string {
  const doc = parseDoc(editorHtml)
  // Expand opaque signature blocks LAST (below) so the default table/cell/img
  // styling pass never touches a pasted signature's own layout — the block is
  // an empty <div data-qte-sig> while the style pass runs, then becomes the
  // verbatim signature HTML for the recipient.
  if (!doc) return sanitizeEmailHtml(expandQuoteBlocks(expandSignatureBlocks(editorHtml)))
  bakeCardChips(doc)
  bakeFormulas(doc)
  pruneEmptyListItems(doc)
  bakeImageAlign(doc)
  doc.querySelectorAll('table').forEach((el) => prependStyle(el, STYLE.table))
  doc.querySelectorAll('th').forEach((el) => prependStyle(el, STYLE.header))
  doc.querySelectorAll('td').forEach((el) => prependStyle(el, STYLE.cell))
  doc.querySelectorAll('h1').forEach((el) => prependStyle(el, STYLE.h1))
  doc.querySelectorAll('h2').forEach((el) => prependStyle(el, STYLE.h2))
  doc.querySelectorAll('h3').forEach((el) => prependStyle(el, STYLE.h3))
  doc.querySelectorAll('ul').forEach((el) => prependStyle(el, listStyleFor(el, false)))
  doc.querySelectorAll('ol').forEach((el) => prependStyle(el, listStyleFor(el, true)))
  doc.querySelectorAll('li').forEach((el) => prependStyle(el, STYLE.li))
  doc.querySelectorAll('blockquote').forEach((el) => prependStyle(el, STYLE.blockquote))
  doc.querySelectorAll('a').forEach((el) => prependStyle(el, STYLE.a))
  doc.querySelectorAll('img').forEach((el) => prependStyle(el, STYLE.img))
  // Expand opaque quote blocks alongside signatures: both carry html the
  // style pass above must never touch; the recipient gets the faithful
  // original (BUG-2026-07-07-EMAIL-QUOTE-TABLE-GRID).
  return sanitizeEmailHtml(expandQuoteBlocks(expandSignatureBlocks(doc.body.innerHTML)))
}

/** Strip `data:image/svg+xml` sources — a known stored-XSS vector (an SVG
 *  data URL can carry <script>). DOMPurify treats a data: URL as an opaque
 *  string so it can't sanitise INSIDE it; we neutralise the whole value
 *  instead (a broken image is the safe failure mode). Raster data: images
 *  (png/jpeg/gif/webp) are inert and kept. */
function stripSvgDataUris(html: string): string {
  return html.replace(
    /\s(?:src|href)\s*=\s*(["'])\s*data:image\/svg\+xml[^"']*\1/gi,
    ' src=""',
  )
}

/** Final allowlist sanitise for outbound HTML. Uses DOMPurify's curated HTML
 *  profile (keeps the formatting + LEGACY table attrs — cellpadding, border,
 *  align, colspan — that quoted reply/forward originals rely on) and layers
 *  on our email needs: drop <style> blocks, keep target + the card hook, and
 *  neutralise SVG data URIs. Scripts, event handlers and javascript:/unknown
 *  protocols are dropped by DOMPurify.
 *
 *  NB: we deliberately do NOT pass a custom ALLOWED_URI_REGEXP — DOMPurify
 *  applies it to EVERY attribute value, which silently strips numeric/keyword
 *  presentational attrs (colspan="2", align="center"). The svg post-pass
 *  covers the only data: type we want to block. */
export function sanitizeEmailHtml(html: string): string {
  if (typeof window === 'undefined') return html
  try {
    const clean = DOMPurify.sanitize(html || '', {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['style'],
      ADD_ATTR: ['target', 'data-card-id'],
    })
    return stripSvgDataUris(clean)
  } catch {
    return html
  }
}
